import type { InputBaseProps, PolymorphicComponentProps } from '@mantine/core';
import {
  ActionIcon,
  Autocomplete,
  Badge,
  Fieldset,
  Input,
  InputBase,
  InputWrapper,
  Modal,
  MultiSelect,
  Paper,
  Pill,
  Select,
  TextInput,
  createTheme,
  defaultVariantColorsResolver,
  colorsTuple as mantineColorsTuple,
  virtualColor as mantineVirtualColor,
  type MantineColorsTuple,
  type MantineThemeOverride,
} from '@mantine/core';
import type { CustomColor, ExtendedColor } from '@util/color';
import { range } from 'radashi';

declare module '@mantine/core' {
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedColor, MantineColorsTuple>;
  }
}

function colorsTuple(c0_9: string): MantineColorsTuple;
function colorsTuple(c0_4: string, c5_9: string): MantineColorsTuple;
function colorsTuple(c0_3: string, c4_5: string, c6_9: string): MantineColorsTuple;
function colorsTuple(...colors: [string] | [string, string] | [string, string, string]): MantineColorsTuple {
  if (colors.length === 1) {
    return mantineColorsTuple(colors[0]);
  }

  if (colors.length === 2) {
    return mantineColorsTuple([...range(0, 4, colors[0]), ...range(5, 9, colors[1])]);
  }

  return mantineColorsTuple([...range(0, 3, colors[0]), ...range(4, 6, colors[1]), ...range(7, 9, colors[2])]);
}

function virtualColor(
  name: CustomColor,
  light: ExtendedColor | MantineColorsTuple,
  dark: ExtendedColor | MantineColorsTuple,
): Record<string, MantineColorsTuple> {
  const colors: Record<string, MantineColorsTuple> = {};

  if (typeof light !== 'string') {
    colors[`${name}-light`] = light;
    light = `${name}-light`;
  }

  if (typeof dark !== 'string') {
    colors[`${name}-dark`] = dark;
    dark = `${name}-dark`;
  }

  colors[name] = mantineVirtualColor({ name, light, dark });

  return colors;
}

const theme: MantineThemeOverride = createTheme({
  black: '#333',

  colors: {
    dark: [
      '#c9c9c9',
      '#5D5F6B',
      '#52545E',
      '#474952',
      '#3C3D45',
      '#313238',
      '#26272B',
      '#1A1B1E',
      '#16171A',
      '#121315',
    ],

    white: ['#fff', '#fff', '#fff', '#fff', '#fff', '#fff', '#fff', '#fff', '#F9F9FB', '#f1f3f5'],

    blue: [
      '#8299FF',
      '#8299FF',
      '#8299FF',
      '#8299FF',
      '#8299FF',
      '#5271FD',
      '#3252E3',
      '#3252E3',
      '#2043df',
      '#2043df',
    ],

    'mouse-over': colorsTuple('#FDDE52'),

    ...virtualColor('status-critical', colorsTuple('#fc8285', '#B50306'), colorsTuple('#fc8285')),
    ...virtualColor('status-warning', colorsTuple('#FCB353', '#A65F03'), colorsTuple('#FCB353')),
    ...virtualColor('status-offline', colorsTuple('#adb5bd', '#4F5863'), colorsTuple('#adb5bd')),
    ...virtualColor('status-ok', colorsTuple('#2ac157', '#15652D'), colorsTuple('#2ac157')),
    ...virtualColor('paper', 'white', 'dark'),
  },

  autoContrast: true,
  primaryColor: 'mouse-over',
  primaryShade: 5,

  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji',

  breakpoints: {
    xs: '30em',
    sm: '48em',
    md: '64em',
    lg: '74em',
    xl: '90em',
  },

  defaultRadius: 'md',

  variantColorResolver: (input) => {
    const defaultResolvedColors = defaultVariantColorsResolver(input);

    if (input.variant === 'transparent') {
      return {
        ...defaultResolvedColors,
        color: 'currentColor',
      };
    }

    if (input.variant === 'default') {
      return {
        ...defaultResolvedColors,
        background: 'transparent',
      };
    }

    return defaultResolvedColors;
  },

  components: {
    Paper: Paper.extend({
      defaultProps: {
        bg: 'paper.7',
      },
    }),

    InputWrapper: InputWrapper.extend({
      defaultProps: {
        size: 'md',
      },
      styles() {
        return {
          label: {
            lineHeight: 1,
            marginBottom: 10,
          },
          required: {
            color: 'inherit',
          },
          description: {
            color: 'var(--mantine-color-dimmed)',
          },
        };
      },
    }),

    Input: Input.extend({
      defaultProps: {
        size: 'md',
      },
      styles() {
        return {
          section: {
            '--input-section-color': 'var(--mantine-color-text)',
          },
        };
      },
    }),

    InputBase: InputBase.extend({
      styles(_theme, props) {
        return {
          input: {
            '--input-cursor':
              (props as PolymorphicComponentProps<'button', InputBaseProps>).component === 'button'
                ? 'pointer'
                : undefined,
          },
        };
      },
    }),

    TextInput: TextInput.extend({
      defaultProps: {
        size: 'md',
      },
    }),

    Autocomplete: Autocomplete.extend({
      defaultProps: {
        size: 'md',
      },
    }),

    Select: Select.extend({
      defaultProps: {
        size: 'md',
        withCheckIcon: false,
      },
      styles(_theme, props) {
        return {
          option: !props.withCheckIcon
            ? {
                '&[dataChecked]': {
                  backgroundColor: 'var(--mantine-primary-color-filled)',
                  color: 'var(--mantine-primary-color-contrast)',
                },
              }
            : {},
        };
      },
    }),

    MultiSelect: MultiSelect.extend({
      defaultProps: {
        size: 'md',
      },
    }),

    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: 'transparent',
      },
    }),

    Pill: Pill.extend({
      styles() {
        return {
          root: {
            minWidth: 'initial',
          },
        };
      },
    }),

    Badge: Badge.extend({
      defaultProps: {
        variant: 'default',
        radius: 'sm',
      },
      styles() {
        return {
          root: {
            textTransform: 'none',
          },
        };
      },
    }),

    Fieldset: Fieldset.extend({
      styles() {
        return {
          root: {
            backgroundColor: 'transparent',
          },
        };
      },
    }),

    Modal: Modal.extend({
      styles: {
        header: {
          backgroundColor: 'transparent',
        },
      },
    }),
  },
});

export default theme;
